import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

interface JobFormData {
  title: string;
  department: string;
  location: string;
  type: string;
  salary: string;
  description: string;
  requirements: string[];
  // Optional per-job custom questions to ask applicants
  questions: string[];
  // Posting date (YYYY-MM-DD) and optional expiry date
  postingDate: string;
  expiryDate?: string;
  isActive: boolean;
}

const JobForm: React.FC = () => {
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState<JobFormData>({
    title: '',
    department: '',
    location: '',
    type: 'Full-time',
    salary: '',
    description: '',
    requirements: [''],
    questions: [],
    postingDate: new Date().toISOString().slice(0, 10),
    expiryDate: '',
    isActive: true,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEdit) {
      fetchJob();
    }
  }, [id]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/jobs/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch job');
      const data = await res.json();
      
      setFormData({
        title: data.title,
        department: data.department,
        location: data.location,
        type: data.type,
        salary: data.salary,
        description: data.description,
        requirements: (data.requirements && data.requirements.length > 0) ? data.requirements : [''],
        questions: Array.isArray(data.questions) ? data.questions : [],
        postingDate: data.postingDate || new Date().toISOString().slice(0, 10),
        expiryDate: data.expiryDate || '',
        isActive: Boolean(data.isActive),
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Filter out empty requirements
    const cleanedRequirements = formData.requirements.filter((req) => req.trim() !== '');
    // Filter out empty questions (optional)
    const cleanedQuestions = (formData.questions || []).filter((q) => q.trim() !== '');

    if (cleanedRequirements.length === 0) {
      setError('Please add at least one requirement');
      return;
    }

    try {
      setLoading(true);
      const url = isEdit ? `${BACKEND_URL}/jobs/${id}` : `${BACKEND_URL}/jobs`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          requirements: cleanedRequirements,
          questions: cleanedQuestions,
        }),
      });

      if (!res.ok) throw new Error('Failed to save job');

      alert(`Job ${isEdit ? 'updated' : 'created'} successfully!`);
      navigate('/admin/jobs');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof JobFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRequirementChange = (index: number, value: string) => {
    const newRequirements = [...formData.requirements];
    newRequirements[index] = value;
    setFormData((prev) => ({ ...prev, requirements: newRequirements }));
  };

  const handleQuestionChange = (index: number, value: string) => {
    const current = formData.questions || [];
    const newQuestions = [...current];
    newQuestions[index] = value;
    setFormData((prev) => ({ ...prev, questions: newQuestions }));
  };

  const addRequirement = () => {
    setFormData((prev) => ({ ...prev, requirements: [...prev.requirements, ''] }));
  };

  const addQuestion = () => {
    setFormData((prev) => ({ ...prev, questions: [...(prev.questions || []), ''] }));
  };

  const removeRequirement = (index: number) => {
    if (formData.requirements.length === 1) return;
    const newRequirements = formData.requirements.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, requirements: newRequirements }));
  };

  const removeQuestion = (index: number) => {
    const current = formData.questions || [];
    const newQuestions = current.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, questions: newQuestions }));
  };

  if (loading && isEdit) {
    return (
      <div 
        className="text-center py-8" 
        style={{ 
          color: 'var(--pw-muted-light)',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}
      >
        Loading job...
      </div>
    );
  }

  return (
    <div className="max-w-4xl" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      <div className="mb-8">
        <h1 
          className="text-3xl font-bold mb-2" 
          style={{ 
            color: 'var(--pw-text-main)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
            letterSpacing: '-0.02em'
          }}
        >
          {isEdit ? 'Edit Job Posting' : 'Create New Job Posting'}
        </h1>
        <p 
          className="text-sm" 
          style={{ 
            color: 'var(--pw-muted-light)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
          }}
        >
          {isEdit ? 'Update job details and requirements' : 'Fill in the job details and requirements'}
        </p>
      </div>

      {error && (
        <div 
          className="mb-6 border px-4 py-3 rounded-lg"
          style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', borderColor: '#ef4444', color: '#ef4444' }}
        >
          {error}
        </div>
      )}

      <form 
        onSubmit={handleSubmit} 
        className="rounded-2xl p-8 space-y-6 backdrop-blur-sm"
        style={{ 
          background: 'var(--pw-gradient-card)',
          border: '1px solid var(--pw-surface-tertiary)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)'
        }}
      >
        {/* Title */}
        <div>
          <label 
            className="block text-sm font-medium mb-2" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
            Job Title *
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
            placeholder="e.g., Senior Full Stack Developer"
            required
          />
        </div>

        {/* Posting & Expiry Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{
                color: 'var(--pw-text-main)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Posting Date *
            </label>
            <input
              type="date"
              value={formData.postingDate}
              onChange={(e) => handleChange('postingDate', e.target.value)}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
              style={{
                backgroundColor: 'var(--pw-surface-secondary)',
                border: '1px solid var(--pw-surface-tertiary)',
                color: 'var(--pw-text-main)'
              }}
              required
            />
          </div>
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{
                color: 'var(--pw-text-main)',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
              }}
            >
              Expiry Date (optional)
            </label>
            <input
              type="date"
              value={formData.expiryDate || ''}
              onChange={(e) => handleChange('expiryDate', e.target.value)}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
              style={{
                backgroundColor: 'var(--pw-surface-secondary)',
                border: '1px solid var(--pw-surface-tertiary)',
                color: 'var(--pw-text-main)'
              }}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--pw-muted-light)' }}>
              After this date, the role will no longer appear on the careers page. Leave blank to keep it open.
            </p>
          </div>
        </div>

        {/* Department & Location */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label 
            className="block text-sm font-medium mb-2" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
              Department *
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
              placeholder="e.g., Engineering"
              required
            />
          </div>
          <div>
            <label 
            className="block text-sm font-medium mb-2" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
              Location *
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => handleChange('location', e.target.value)}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
              placeholder="e.g., Remote / Islamabad"
              required
            />
          </div>
        </div>

        {/* Type & Salary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label 
            className="block text-sm font-medium mb-2" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
              Employment Type *
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
              required
            >
              <option value="Full-time">Full-time</option>
              <option value="Part-time">Part-time</option>
              <option value="Contract">Contract</option>
              <option value="Internship">Internship</option>
            </select>
          </div>
          <div>
            <label 
            className="block text-sm font-medium mb-2" 
            style={{ 
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
          >
              Salary Range (optional)
            </label>
            <input
              type="text"
              value={formData.salary}
              onChange={(e) => handleChange('salary', e.target.value)}
              className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
              placeholder="e.g., ₨ 150k - ₨ 250k or Competitive (leave empty to post without salary)"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Job Description *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            className="w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors min-h-[120px]"
            style={{ 
              backgroundColor: 'var(--pw-surface-secondary)',
              border: '1px solid var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)'
            }}
            placeholder="Describe the role, responsibilities, and what makes this position unique..."
            required
          />
        </div>

        {/* Requirements */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Requirements *
          </label>
          <div className="space-y-3">
            {formData.requirements.map((req, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={req}
                  onChange={(e) => handleRequirementChange(index, e.target.value)}
                  className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
                  style={{ 
                    backgroundColor: 'var(--pw-surface-secondary)',
                    border: '1px solid var(--pw-surface-tertiary)',
                    color: 'var(--pw-text-main)'
                  }}
                  placeholder="e.g., 3+ years of React experience"
                />
                {formData.requirements.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeRequirement(index)}
                    className="p-3 rounded-lg transition-colors"
                    style={{ color: '#ef4444', backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addRequirement}
            className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{ 
              color: 'var(--pw-primary-light)',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Plus className="w-4 h-4" />
            Add Requirement
          </button>
        </div>

        {/* Application Questions (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Application Questions (optional)
          </label>
          <p className="text-xs mb-3" style={{ color: 'var(--pw-muted-light)' }}>
            These questions will be shown on the application form for this role. You can leave this empty, or add as many questions as you like.
          </p>
          <div className="space-y-3">
            {(formData.questions || []).map((q, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={q}
                  onChange={(e) => handleQuestionChange(index, e.target.value)}
                  className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--pw-primary)] transition-colors"
                  style={{
                    backgroundColor: 'var(--pw-surface-secondary)',
                    border: '1px solid var(--pw-surface-tertiary)',
                    color: 'var(--pw-text-main)'
                  }}
                  placeholder="e.g., Why do you want to work at PattrnWorks?"
                />
                <button
                  type="button"
                  onClick={() => removeQuestion(index)}
                  className="p-3 rounded-lg transition-colors"
                  style={{ color: '#ef4444', backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={addQuestion}
            className="mt-3 flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
            style={{
              color: 'var(--pw-primary-light)',
              backgroundColor: 'transparent'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        </div>

        {/* Status */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="isActive"
            checked={formData.isActive}
            onChange={(e) => handleChange('isActive', e.target.checked)}
            className="w-5 h-5 rounded"
            style={{ 
              accentColor: 'var(--pw-primary)',
              borderColor: 'var(--pw-surface-tertiary)'
            }}
          />
          <label htmlFor="isActive" className="text-sm font-medium" style={{ color: 'var(--pw-text-main)' }}>
            Publish this job posting (make it visible on careers page)
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4" style={{ borderTop: '1px solid var(--pw-surface-tertiary)' }}>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-200 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              background: 'linear-gradient(135deg, var(--pw-primary) 0%, var(--pw-primary-light) 100%)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.opacity = '0.9';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.opacity = '1';
            }}
          >
            <Save className="w-5 h-5" />
            {loading ? 'Saving...' : (isEdit ? 'Update Job' : 'Create Job')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/jobs')}
            className="px-6 py-3 rounded-xl transition-all duration-200 font-medium"
            style={{ 
              backgroundColor: 'var(--pw-surface-tertiary)',
              color: 'var(--pw-text-main)',
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--pw-surface-tertiary)';
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;

